import * as rrweb from "rrweb";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import * as FingerprintJS from "@fingerprintjs/fingerprintjs";
import {
  SessionableOptions,
  SessionMetrics,
  SessionPayload,
  UserIdentification,
  IdentifyPayload,
} from "../types";
import { listenerHandler } from "@rrweb/types";

/**
 * Core Sessionable class for session recording
 */
export class Sessionable {
  private options: SessionableOptions & {
    apiKey: string;
    autoStart: boolean;
    metadata: Record<string, any>;
    maskInputs: boolean;
    maskTextSelector?: string;
    enableAnonymousId: boolean;
    apiEndpoint: string;
    devMode: boolean;
  };
  private events: rrweb.eventWithTime[] = [];
  private stopFn: listenerHandler | null = null;
  private eventsInterval: ReturnType<typeof setInterval> | null = null;
  private metrics: SessionMetrics;
  // User identification details
  private userIdentity: UserIdentification = {};
  // FingerprintJS instance
  private fpPromise: Promise<FingerprintJS.Agent> | null = null;
  // API endpoints
  private sessionsApiEndpoint: string;
  private identifyApiEndpoint: string;
  // Batch configuration
  private batchSize = 50;
  private batchInterval = 5000; // ms
  // LocalStorage keys
  private static readonly SESSION_ID_KEY = "sessionable_session_id";
  private static readonly ANONYMOUS_ID_KEY = "sessionable_anonymous_id";
  // Default API endpoints
  private static readonly PROD_API_BASE = "https://sessionable.pages.dev";
  private static readonly DEV_API_BASE = "http://localhost:5173";

  /**
   * Creates a new Sessionable instance
   */
  constructor(options: SessionableOptions) {
    // Determine API base URL
    const devMode = options.devMode === true;
    const apiEndpoint = options.apiEndpoint || 
                        (devMode ? Sessionable.DEV_API_BASE : Sessionable.PROD_API_BASE);
    
    // Set default options
    this.options = {
      apiKey: options.apiKey,
      autoStart: options.autoStart || false,
      metadata: options.metadata || {},
      maskInputs: options.maskInputs || false,
      maskTextSelector: options.maskTextSelector,
      enableAnonymousId: options.enableAnonymousId !== false, // Default to true
      apiEndpoint: apiEndpoint,
      devMode: devMode,
    };

    // Set API endpoints
    this.sessionsApiEndpoint = `${this.options.apiEndpoint}/api/sessions`;
    this.identifyApiEndpoint = `${this.options.apiEndpoint}/api/identify`;
    
    // Log the API endpoints in dev mode
    if (this.options.devMode) {
      console.log(`[Sessionable] Using API endpoints:`, {
        sessions: this.sessionsApiEndpoint,
        identify: this.identifyApiEndpoint
      });
    }

    // Initialize metrics
    this.metrics = {
      sessionId: "",
      eventsRecorded: 0,
      batchesSent: 0,
      batchesSuccessful: 0,
      connectionStatus: "disconnected",
      lastError: null,
      lastBatchSize: 0,
      lastSuccessfulUpload: null,
      recordingStartTime: new Date(),
      isRecording: false,
    };

    // Initialize user identity from options if provided
    if (options.user) {
      this.userIdentity = { ...options.user };
    }

    // Initialize FingerprintJS if anonymous ID is enabled or not already provided
    // This is now critical for the new identity system which requires anonymous IDs
    if ((this.options.enableAnonymousId && !this.userIdentity.anonymousId) || 
        (!this.userIdentity.anonymousId)) {
      // Start fingerprinting immediately
      this.initializeFingerprinting();
    }

    // Note: autoStart is now handled by the start() method which is async
    // We'll no longer auto-start here as the React component will handle it
  }

  /**
   * Generate a human-readable ID from the input hash
   * Creates IDs like "purple-tiger-42" or "green-elephant-7"
   */
  private generateReadableId(hash: string): string {
    // List of adjectives and animals for readable IDs
    const adjectives = [
      "red", "blue", "green", "yellow", "purple", 
      "brave", "calm", "eager", "fierce", "gentle",
      "happy", "jolly", "kind", "lively", "mighty",
      "nice", "proud", "quiet", "swift", "wise"
    ];
    
    const animals = [
      "panda", "tiger", "wolf", "eagle", "dolphin",
      "lion", "bear", "fox", "owl", "shark",
      "whale", "turtle", "monkey", "zebra", "elephant",
      "koala", "penguin", "rabbit", "deer", "falcon"
    ];
    
    // Use part of the hash to deterministically select words
    const adjectiveIndex = parseInt(hash.substring(0, 8), 16) % adjectives.length;
    const animalIndex = parseInt(hash.substring(8, 16), 16) % animals.length;
    
    // Use another part for a short number (1-99)
    const number = (parseInt(hash.substring(16, 24), 16) % 99) + 1;
    
    // Combine to create the readable ID
    return `${adjectives[adjectiveIndex]}-${animals[animalIndex]}-${number}`;
  }

  /**
   * Initialize fingerprinting for anonymous identification
   */
  private async initializeFingerprinting(): Promise<void> {
    // Try to load from localStorage first
    try {
      const storedId = localStorage.getItem(Sessionable.ANONYMOUS_ID_KEY);
      if (storedId) {
        this.userIdentity.anonymousId = storedId;
        return;
      }
    } catch (e) {
      // Ignore localStorage errors
    }

    // If no stored ID, use FingerprintJS
    try {
      // Initialize FingerprintJS
      this.fpPromise = FingerprintJS.load();

      // Get the visitor identifier
      const fp = await this.fpPromise;
      const result = await fp.get();
      
      // Generate a readable ID based on the fingerprint
      const readableId = this.generateReadableId(result.visitorId);
      
      // Use the readable ID with anon- prefix
      this.userIdentity.anonymousId = `anon-${readableId}`;

      // Store in localStorage if available
      try {
        localStorage.setItem(Sessionable.ANONYMOUS_ID_KEY, this.userIdentity.anonymousId);
      } catch (e) {
        // Ignore localStorage errors
      }
    } catch (error) {
      console.warn("[Sessionable] Failed to get fingerprint:", error);

      // Fallback to UUID if fingerprinting fails
      const uuid = uuidv4();
      const readableId = this.generateReadableId(uuid);
      const fallbackId = `anon-${readableId}`;
      this.userIdentity.anonymousId = fallbackId;

      // Try to store in localStorage
      try {
        localStorage.setItem(Sessionable.ANONYMOUS_ID_KEY, fallbackId);
      } catch (e) {
        // Ignore localStorage errors
      }
    }
  }

  /**
   * Identify a user for the current session
   *
   * This will store the user identity immediately and include it with all future event batches.
   * If a session is currently active, it will also make an API call to link the user to the session.
   * If no session is active, the identity will be applied when the session starts.
   */
  public identify(
    userId: string,
    options: {
      organizationId?: string;
      userAttributes?: Record<string, any>;
    } = {}
  ): void {
    // Set user identity properties
    this.userIdentity.userId = userId;

    if (options.organizationId !== undefined) {
      this.userIdentity.organizationId = options.organizationId;
    }

    if (options.userAttributes) {
      this.userIdentity.userAttributes = {
        ...(this.userIdentity.userAttributes || {}),
        ...options.userAttributes,
      };
    }

    // If a session is active, call the identify API
    if (this.metrics.sessionId && this.metrics.isRecording) {
      this.callIdentifyApi(userId, options).catch((error) => {
        console.error("[Sessionable] Failed to identify user:", error);
      });
    }
  }

  /**
   * Make the API call to identify a user with the current session
   * This is separated from the identify method to allow for async handling
   * without blocking the main identify call
   */
  private async callIdentifyApi(
    userId: string,
    options: {
      organizationId?: string;
      userAttributes?: Record<string, any>;
    }
  ): Promise<void> {
    try {
      // Ensure we have an anonymous ID before identifying the user
      if (!this.userIdentity.anonymousId) {
        await this.initializeFingerprinting();
      }

      const payload: IdentifyPayload = {
        session_id: this.metrics.sessionId,
        user_id: userId,
        anonymous_id: this.userIdentity.anonymousId, // Include anonymous ID for proper mapping
      };

      // Add optional fields
      if (options.organizationId !== undefined) {
        payload.organization_id = options.organizationId;
      }

      if (
        options.userAttributes &&
        Object.keys(options.userAttributes).length > 0
      ) {
        payload.user_attributes = options.userAttributes;
      }

      // Send identify request to API
      await axios.post(this.identifyApiEndpoint, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.options.apiKey}`,
        },
      });
    } catch (error) {
      console.error("[Sessionable] Failed to identify user:", error);
      throw error;
    }
  }

  /**
   * Get the current anonymous ID
   */
  public getAnonymousId(): string | undefined {
    return this.userIdentity.anonymousId;
  }

  /**
   * Start recording the session
   */
  public async start(): Promise<void> {
    if (this.metrics.isRecording) return;

    // Ensure we have an anonymous ID before starting the session
    // This is critical for the new identity system which requires anonymous IDs
    if (!this.userIdentity.anonymousId) {
      try {
        await this.initializeFingerprinting();
      } catch (error) {
        console.error("[Sessionable] Failed to initialize fingerprinting:", error);
        // We'll still continue with session recording as we should have a fallback ID
      }
    }

    // Verify we have an anonymous ID after fingerprinting
    if (!this.userIdentity.anonymousId) {
      console.error("[Sessionable] Cannot start session without anonymous ID");
      return;
    }

    // Generate a new session ID
    const sessionId = uuidv4();

    // Store locally if localStorage is available
    try {
      localStorage.setItem(Sessionable.SESSION_ID_KEY, sessionId);
    } catch (e) {
      // Ignore localStorage errors
    }

    // Reset events array
    this.events = [];

    // Update metrics
    this.updateMetrics({
      sessionId,
      recordingStartTime: new Date(),
      eventsRecorded: 0,
      batchesSent: 0,
      batchesSuccessful: 0,
      connectionStatus: "connecting",
      isRecording: true,
    });

    // Start recording with rrweb
    const stop = rrweb.record({
      emit: (event) => {
        // Push event to array
        this.events.push(event);

        // Update metrics
        this.updateMetrics({
          eventsRecorded: this.metrics.eventsRecorded + 1,
        });

        // Send batch if we've reached the threshold
        if (this.events.length >= this.batchSize) {
          const eventsToSend = [...this.events];
          this.sendEvents(eventsToSend);
          this.events = [];
        }
      },
      // Configure rrweb options based on user settings
      inlineStylesheet: true,
      maskAllInputs: this.options.maskInputs,
      maskTextSelector: this.options.maskTextSelector,
      recordCanvas: true,
      collectFonts: true,
    });

    // Set up interval to periodically send events
    this.eventsInterval = setInterval(() => {
      if (this.events.length > 0) {
        const eventsToSend = [...this.events];
        this.sendEvents(eventsToSend);
        this.events = [];
      }
    }, this.batchInterval);

    // Save stop function for later
    this.stopFn = stop || null;

    // If we have a user ID, make identify API call now that session is active
    if (this.userIdentity.userId) {
      this.callIdentifyApi(this.userIdentity.userId, {
        organizationId: this.userIdentity.organizationId,
        userAttributes: this.userIdentity.userAttributes,
      }).catch((error) => {
        console.error(
          "[Sessionable] Failed to identify user during session start:",
          error
        );
      });
    }
  }

  /**
   * Stop recording the session
   */
  public stop(): void {
    if (!this.metrics.isRecording || !this.stopFn) return;

    // Stop recording
    this.stopFn();
    this.stopFn = null;

    // Clear interval
    if (this.eventsInterval) {
      clearInterval(this.eventsInterval);
      this.eventsInterval = null;
    }

    // Send any remaining events
    if (this.events.length > 0) {
      const finalEvents = [...this.events];
      this.events = [];
      this.sendEvents(finalEvents);
    }

    // Update metrics
    this.updateMetrics({
      isRecording: false,
    });
  }

  /**
   * Get current session metrics
   */
  public getMetrics(): SessionMetrics {
    // Make sure we create a proper copy with Date objects intact
    return {
      ...this.metrics,
      // Ensure recordingStartTime remains a Date object
      recordingStartTime: new Date(this.metrics.recordingStartTime),
      lastSuccessfulUpload: this.metrics.lastSuccessfulUpload
        ? new Date(this.metrics.lastSuccessfulUpload)
        : null,
    };
  }

  /**
   * Add custom metadata to the session
   */
  public addMetadata(metadata: Record<string, any>): void {
    this.options.metadata = {
      ...this.options.metadata,
      ...metadata,
    };
  }

  /**
   * Send events to the Sessionable API
   */
  private async sendEvents(events: rrweb.eventWithTime[]): Promise<void> {
    if (events.length === 0) return;

    // Verify we have an anonymous ID - it's required for the new identity system
    if (!this.userIdentity.anonymousId) {
      try {
        await this.initializeFingerprinting();
        if (!this.userIdentity.anonymousId) {
          console.error("[Sessionable] Cannot send events without anonymous ID");
          return;
        }
      } catch (error) {
        console.error("[Sessionable] Failed to get anonymous ID:", error);
        this.updateMetrics({
          connectionStatus: "error",
          lastError: "Failed to get anonymous ID",
        });
        return;
      }
    }

    this.updateMetrics({
      lastBatchSize: events.length,
      batchesSent: this.metrics.batchesSent + 1,
    });

    try {
      // Prepare metadata with basic information
      const metadata = {
        ...this.options.metadata,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        eventCount: events.length,
      };

      // Prepare payload according to API format - anonymous_id is now required
      const payload: SessionPayload = {
        session_id: this.metrics.sessionId,
        events: events,
        metadata: metadata,
        anonymous_id: this.userIdentity.anonymousId, // Always include anonymous ID
      };

      // Add user identification data if available
      if (this.userIdentity.userId) {
        payload.user_id = this.userIdentity.userId;
        console.log("Adding user_id to payload:", payload.user_id);
      }

      if (this.userIdentity.organizationId !== undefined) {
        payload.organization_id = this.userIdentity.organizationId;
        console.log(
          "Adding organization_id to payload:",
          payload.organization_id
        );
      }

      if (
        this.userIdentity.userAttributes &&
        Object.keys(this.userIdentity.userAttributes).length > 0
      ) {
        payload.user_attributes = this.userIdentity.userAttributes;
        console.log(
          "Adding user_attributes to payload:",
          payload.user_attributes
        );
      }

      console.log("Sending payload to API:", {
        ...payload,
        events: `[${events.length} events]`, // Don't log the entire events array
      });

      // Send to API
      const response = await axios.post(this.sessionsApiEndpoint, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.options.apiKey}`,
        },
      });

      console.log("API response:", response.status, response.data);

      // Update metrics on success
      this.updateMetrics({
        connectionStatus: "connected",
        batchesSuccessful: this.metrics.batchesSuccessful + 1,
        lastSuccessfulUpload: new Date(),
        lastError: null,
      });
    } catch (error) {
      // Update metrics on error
      this.updateMetrics({
        connectionStatus: "error",
        lastError: error instanceof Error ? error.message : "Unknown error",
      });
      console.error("[Sessionable] Failed to send session data:", error);
    }
  }

  /**
   * Update session metrics
   */
  private updateMetrics(updates: Partial<SessionMetrics>): void {
    this.metrics = {
      ...this.metrics,
      ...updates,
    };
  }
}
